import React, { useEffect, useRef, useState } from "react";
import { buildScoringTiers } from "../utils";
import { SearchIcon, XIcon } from "./Icons";

// Tier thresholds come from the recommended-threshold sheet (Apps column).
const scoringTiers = buildScoringTiers("APPS", [
	{
		key: "bestMatch",
		label: "Best Match",
		meaning: "The most accurate results.",
		warning: false,
	},
	{
		key: "strongMatch",
		label: "Strong Match",
		meaning: "Very relevant results.",
		warning: false,
	},
	{
		key: "acceptable",
		label: "Acceptable",
		meaning: "Results are generally relevant.",
		warning: false,
	},
	{
		key: "extended",
		label: "Extended",
		meaning: "Includes less relevant results.",
		warning: true,
	},
	{
		key: "maximumReach",
		label: "Maximum Reach",
		meaning: "Shows all qualifying results; many may have low relevance.",
		warning: true,
	},
]);

export const AppSearchInterface = ({
	// Props from useAppSearchInterface hook
	searchQuery,
	refinedQuery,
	isSearching,
	performanceMetric: hookPerformanceMetric,
	minReachThreshold: hookMinReachThreshold,

	// Event handlers from hook
	onSearchInputChange,
	onRefinedQueryChange,
	onSearch,
	onFiltersChange,
	onClearRefinement,
	onPerformanceMetricChange,
	onMinReachChange,

	// Additional props
	filters = {},
	className = "",

	// NEW: Props for search terms persistence
	searchTerms = [],
	onSearchTermsChange,
	initialSortBy = "compositeScore",
	initialSortOrder = "desc",
	onSortChange,
}) => {
	// ✅ UPDATED: Use searchTerms from props instead of local state
	const [performanceMetric, setPerformanceMetric] = useState(
		hookPerformanceMetric || "impressions",
	);
	const [minValue, setMinValue] = useState(hookMinReachThreshold || "100"); // ✅ Only market requests value

	// ✅ NEW: Replace currentScore with selectedTier
	const [selectedTier, setSelectedTier] = useState(scoringTiers[1]); // Default to "Strong Match" (sheet default)

	// ✅ NEW: Sort functionality
	const [sortBy, setSortBy] = useState(initialSortBy);
	const [sortOrder, setSortOrder] = useState(initialSortOrder);

	// App-specific performance metrics
	const performanceMetricOptions = [
		{ value: "viewability", label: "Viewability" },
		{ value: "ctr", label: "CTR (Click-Through Rate)" },
		{ value: "vtr", label: "VTR (View-Through Rate)" },
		{ value: "reach", label: "Market Request" },
		{ value: "impressions", label: "Impressions" },
	];

	// ✅ NEW: Sort options for apps
	const sortOptions = [
		{ value: "totalImpressions", label: "Impressions" },
		{ value: "score", label: "Relevance" },
		// { value: 'downloads', label: 'Downloads' },
		// { value: 'rating', label: 'Rating' },
		{ value: "avgViewability", label: "Viewability" },
		{ value: "calculated_ctr", label: "CTR" },
		{ value: "compositeScore", label: "Composite Score" },
	];

	// ✅ NEW: Simplified TierDropdown component
	const TierDropdown = ({ selectedTier, onTierChange, scoringTiers }) => {
		const [showTooltip, setShowTooltip] = useState(false);

		const handleTierSelect = (e) => {
			const tierLabel = e.target.value;
			const tier = scoringTiers.find((t) => t.label === tierLabel);
			onTierChange(tier);
		};

		return (
			<div className="tier-dropdown-container">
				<div className="tier-dropdown-wrapper">
					<select
						value={selectedTier.label}
						onChange={handleTierSelect}
						className="tier-simple-select"
					>
						{scoringTiers.map((tier) => (
							<option key={tier.label} value={tier.label}>
								{tier.label}
							</option>
						))}
					</select>

					<div
						className="info-icon-container"
						onMouseEnter={() => setShowTooltip(true)}
						onMouseLeave={() => setShowTooltip(false)}
					>
						<span className="info-icon">i</span>

						{showTooltip && (
							<div className="tier-tooltip">
								<div className="tooltip-header">
									<strong>Scoring Tiers</strong>
								</div>
								<div className="tooltip-content">
									{scoringTiers.map((tier, index) => (
										<div
											key={index}
											className={`tooltip-tier ${tier.warning ? "warning" : ""}`}
										>
											<div className="tooltip-tier-header">
												<span className="tooltip-tier-label">{tier.label}</span>
												<span className="tooltip-tier-score">
													{tier.score}+
												</span>
											</div>
											<div className="tooltip-tier-meaning">
												{tier.warning && (
													<span className="warning-indicator">⚠️ </span>
												)}
												{tier.meaning}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				<style jsx>{`
        .tier-dropdown-container {
          position: relative;
          width: 100%;
          min-width: 180px;
        }

        .tier-dropdown-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tier-simple-select {
          flex: 1;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          color: #374151;
          font-weight: 600;
        }

        .tier-simple-select:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .tier-simple-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .info-icon-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: help;
        }

        .info-icon {
          font-size: 16px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .info-icon-container:hover .info-icon {
          opacity: 1;
        }

        .tier-tooltip {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 12px;
          z-index: 1000;
          min-width: 320px;
          max-width: 400px;
        }

        .tooltip-header {
          text-align: center;
          margin-bottom: 12px;
          font-size: 14px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 8px;
        }

        .tooltip-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .tooltip-tier {
          padding: 8px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .tooltip-tier.warning {
          background: #fefce8;
          border-color: #fde047;
        }

        .tooltip-tier-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .tooltip-tier-label {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }

        .tooltip-tier-score {
          font-size: 12px;
          font-weight: 700;
          color: #059669;
          background: #dcfce7;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-score {
          color: #d97706;
          background: #fef3c7;
        }

        .tooltip-tier-meaning {
          font-size: 11px;
          color: #6b7280;
          line-height: 1.4;
          display: flex;
          align-items: flex-start;
          gap: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-meaning {
          color: #92400e;
        }

        .warning-indicator {
          font-size: 10px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .tier-tooltip::after {
          content: '';
          position: absolute;
          bottom: 100%;
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid white;
        }

        .tier-tooltip::before {
          content: '';
          position: absolute;
          bottom: calc(100% + 1px);
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #d1d5db;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .tier-tooltip {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            right: auto;
            bottom: auto;
            width: calc(100vw - 32px);
            max-width: 350px;
            max-height: 60vh;
            overflow-y: auto;
          }

          .tier-tooltip::before,
          .tier-tooltip::after {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .tier-dropdown-container {
            min-width: auto;
          }

          .tier-simple-select {
            font-size: 12px;
            padding: 10px;
          }

          .tier-tooltip {
            width: calc(100vw - 20px);
            max-height: 50vh;
            min-width: auto;
          }

          .tooltip-tier-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .tooltip-tier-score {
            align-self: flex-start;
          }
        }
      `}</style>
			</div>
		);
	};

	// Sync with hook state using renamed props
	useEffect(() => {
		if (hookPerformanceMetric && hookPerformanceMetric !== performanceMetric) {
			setPerformanceMetric(hookPerformanceMetric);
		}
	}, [hookPerformanceMetric, performanceMetric]);

	useEffect(() => {
		if (hookMinReachThreshold && hookMinReachThreshold !== minValue) {
			setMinValue(hookMinReachThreshold);
		}
	}, [hookMinReachThreshold, minValue]);

	// ✅ UPDATED: Use selectedTier.score and update parent state
	const handleAddTermDirect = (termType) => {
		if (searchQuery && searchQuery.trim()) {
			const newTerm = {
				id: Date.now(),
				text: searchQuery.trim(),
				score: selectedTier.score, // display relevance %
				threshold: selectedTier.threshold, // exact cosine distance for the API
				type: termType,
			};

			const updatedTerms = [...searchTerms, newTerm];

			// ✅ Update parent state for persistence
			if (onSearchTermsChange) {
				onSearchTermsChange(updatedTerms);
			}

			// Clear input by calling hook's handler
			if (onSearchInputChange) {
				onSearchInputChange({ target: { value: "" } });
			}
		}
	};

	const handleSearchInputChange = (e) => {
		// Call the original handler from the hook
		if (onSearchInputChange) {
			onSearchInputChange(e);
		}
	};

	const handleSearchKeyPress = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAddTermDirect("include");
		}
	};

	// ✅ UPDATED: Update parent state when removing terms
	const removeTerm = (termId) => {
		const updatedTerms = searchTerms.filter((term) => term.id !== termId);
		if (onSearchTermsChange) {
			onSearchTermsChange(updatedTerms);
		}
	};

	const handlePerformanceMetricChange = (e) => {
		const newMetric = e.target.value;
		setPerformanceMetric(newMetric);

		if (onPerformanceMetricChange) {
			onPerformanceMetricChange(newMetric);
		}
	};

	// ✅ SIMPLIFIED: Only handle market requests value change
	const handleMinValueChange = (e) => {
		const value = e.target.value;
		setMinValue(value);

		if (onMinReachChange) {
			onMinReachChange(value);
		}
	};

	// ✅ ADD: Sync local state when props change (for LLM results)
	useEffect(() => {
		console.log("📊 AppSearchInterface: Syncing sort from props:", {
			initialSortBy,
			initialSortOrder,
		});
		setSortBy(initialSortBy);
		setSortOrder(initialSortOrder);
	}, [initialSortBy, initialSortOrder]);

	// ✅ MODIFY: Notify parent when local sort changes
	const handleSortByChange = (e) => {
		const newSortBy = e.target.value;
		console.log("🔧 App Search Interface: Sort by changed to:", newSortBy);
		setSortBy(newSortBy);
		if (onSortChange) {
			onSortChange(newSortBy, sortOrder);
		}
	};

	const handleSortOrderToggle = () => {
		const newOrder = sortOrder === "desc" ? "asc" : "desc";
		console.log("🔧 App Search Interface: Sort order changed to:", newOrder);
		setSortOrder(newOrder);
		if (onSortChange) {
			onSortChange(sortBy, newOrder);
		}
	};

	// ✅ FIXED: Updated handleSearchClick method with proper sorting parameter passing
	const handleSearchClick = () => {
		console.log("🚀 FIXED: handleSearchClick executing with result limit:", {
			sortBy: sortBy,
			sortOrder: sortOrder,
			performanceMetric: performanceMetric,
			minValue: minValue,
			resultLimit: filters?.limit || 500, // ✅ FIXED: Log the result limit
		});

		const actualMarketRequests = (parseFloat(minValue) || 100) * 1000000;

		const enhancedFilters = {
			...filters, // Keep all existing filters

			// ✅ FIXED: Only market requests fields (no target type)
			minMarketRequests: actualMarketRequests, // Now: 100,000,000 instead of 100
			marketRequestsThreshold: actualMarketRequests, // Now: 100,000,000 instead of 100
			minValue: actualMarketRequests,

			// ✅ FIXED: Sorting parameters
			sortBy: sortBy,
			sortOrder: sortOrder,
			view: sortOrder,

			// ✅ FIXED: Result limit - ensure it's passed correctly
			noOfResult: filters?.limit || 5000, // ✅ API expects noOfResult
			limit: filters?.limit || 5000, // ✅ Keep both for compatibility

			// App-specific filter properties
			appSubcategory: [],
			developer: filters.developer || [],
			contentRating: filters.contentRating || [],
			appKeywords: filters.appKeywords || [],
			rating: filters.rating || 0,
			downloads: filters.downloads || null,
			marketSource: filters.marketSource || [],
			platform: filters.platform || [],
		};

		const searchOptions = {
			performanceMetric: performanceMetric,
			minValue: actualMarketRequests,
			performanceGoalType: "reach",

			// ✅ FIXED: Sorting parameters
			sortBy: sortBy,
			sortOrder: sortOrder,

			// ✅ FIXED: Result limit in search options
			noOfResult: filters?.limit || 5000, // ✅ Pass result limit to search options
			limit: filters?.limit || 5000, // ✅ Keep both for compatibility

			dateRange: 30,
			filters: enhancedFilters,
		};

		console.log("🚀 FIXED: Executing app search with result limit:", {
			performanceMetric: performanceMetric,
			marketRequestsInMillions: minValue,
			sortBy: sortBy,
			sortOrder: sortOrder,
			resultLimit: filters?.limit || 5000, // ✅ FIXED: Log result limit
			enhancedFilters: {
				noOfResult: enhancedFilters.noOfResult,
				limit: enhancedFilters.limit,
				sortBy: enhancedFilters.sortBy,
				sortOrder: enhancedFilters.sortOrder,
			},
			searchOptions: {
				noOfResult: searchOptions.noOfResult,
				limit: searchOptions.limit,
				sortBy: searchOptions.sortBy,
				sortOrder: searchOptions.sortOrder,
			},
		});

		if (onSearch) {
			onSearch(searchTerms, searchOptions);
		}
	};

	// ✅ UPDATED: Update parent state when clearing all terms
	const handleClearAll = () => {
		if (onSearchTermsChange) {
			onSearchTermsChange([]);
		}
		if (onSearchInputChange) {
			onSearchInputChange({ target: { value: "" } });
		}
	};

	const handleSearchInputFocus = () => {
		// Just focus, no suggestions for now
	};

	return (
		<div className={`app-search-interface-container ${className}`}>
			{/* Search Terms Display Box */}
			{searchTerms.length > 0 && (
				<div className="search-terms-display">
					<div className="search-terms-content">
						<div className="current-search-term">
							<span>Current app search: </span>
							{searchTerms.map((term, index) => (
								<span key={term.id}>
									<span
										className={`search-term ${term.type === "include" ? "term-include" : "term-exclude"}`}
									>
										{term.type === "include" ? "+" : "-"}
										{term.text} ({term.score})
										<button
											onClick={() => removeTerm(term.id)}
											className="term-remove-btn"
										>
											×
										</button>
									</span>
									{index < searchTerms.length - 1 && <span> </span>}
								</span>
							))}
						</div>
					</div>
					<button className="btn-clear-all-inline" onClick={handleClearAll}>
						Clear All
					</button>
				</div>
			)}

			{/* Single Search Row */}
			<div className="search-input-row">
				<div className="search-input-wrapper">
					<SearchIcon />
					<input
						type="text"
						placeholder="try searching for fitness apps, gaming apps, social media apps..."
						value={searchQuery || ""}
						onChange={handleSearchInputChange}
						onKeyPress={handleSearchKeyPress}
						onFocus={handleSearchInputFocus}
						className="search-input"
					/>

					<div className="term-type-buttons">
						<button
							type="button"
							onClick={() => handleAddTermDirect("include")}
							className="term-type-btn include-btn"
							disabled={!searchQuery || !searchQuery.trim()}
						>
							Include
						</button>
						<button
							type="button"
							onClick={() => handleAddTermDirect("exclude")}
							className="term-type-btn exclude-btn"
							disabled={!searchQuery || !searchQuery.trim()}
						>
							Exclude
						</button>
					</div>
				</div>

				{/* ✅ TierDropdown for search term scoring */}
				<div className="tier-input-container">
					<TierDropdown
						selectedTier={selectedTier}
						onTierChange={(tier) => setSelectedTier(tier)}
						scoringTiers={scoringTiers}
					/>
				</div>

				<div className="search-button-container">
					<button
						type="button"
						onClick={handleSearchClick}
						className="search-button"
						disabled={isSearching}
					>
						{isSearching ? (
							<>
								<div className="loading-spinner-small"></div>
								Searching...
							</>
						) : (
							"Search"
						)}
					</button>
				</div>
			</div>

			{/* ✅ SIMPLIFIED: Performance Filter Section */}
			<div className="performance-filter-section">
				<div className="performance-filter-row">
					<div className="performance-filter-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">
								Performance KPI
							</label>
						</div>
						<select
							value={performanceMetric}
							onChange={handlePerformanceMetricChange}
							className="performance-filter-select"
						>
							{performanceMetricOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>

					{/* ✅ SIMPLIFIED: Only Market Requests Input - No dropdown */}
					<div className="target-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">
								Target Market Requests (in Billions)
							</label>
						</div>
						<div className="target-input-group">
							<input
								type="number"
								value={minValue}
								onChange={handleMinValueChange}
								className="target-value-input"
								placeholder="100"
							/>
						</div>
					</div>

					{/* ✅ Sort By Section with Arrow */}
					<div className="sort-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">Sort by</label>
						</div>
						<div className="sort-controls">
							<select
								value={sortBy}
								onChange={handleSortByChange}
								className="sort-select"
							>
								{sortOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
							<span
								onClick={handleSortOrderToggle}
								className="sort-order-arrow"
								title={sortOrder === "desc" ? "Descending" : "Ascending"}
							>
								{sortOrder === "desc" ? "↓" : "↑"}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Enhanced Quick Filters Display - App Specific - Reordered */}
			<div className="quick-filters">
				{/* 1. TARGETING FILTERS */}

				{/* Media Subtype */}
				{filters?.mediaSubtype?.length > 0 && (
					<span className="quick-filter">
						Format:{" "}
						{filters.mediaSubtype.length > 2
							? `${filters.mediaSubtype.slice(0, 2).join(", ")} +${filters.mediaSubtype.length - 2} more`
							: filters.mediaSubtype.join(", ")}
					</span>
				)}

				{/* Device Type */}
				{filters?.deviceTypes?.length > 0 && (
					<span className="quick-filter">
						Device:{" "}
						{filters.deviceTypes.length > 2
							? `${filters.deviceTypes.slice(0, 2).join(", ")} +${filters.deviceTypes.length - 2} more`
							: filters.deviceTypes.join(", ")}
					</span>
				)}

				{/* Video Placement */}
				{filters?.videoplcmt?.length > 0 && (
					<span className="quick-filter">
						Placement:{" "}
						{filters.videoplcmt.length > 2
							? `${filters.videoplcmt.slice(0, 2).join(", ")} +${filters.videoplcmt.length - 2} more`
							: filters.videoplcmt.join(", ")}
					</span>
				)}

				{(() => {
					const geography = filters?.geography;
					if (!geography) return null;

					// Handle both string and array cases
					const geographyArray = Array.isArray(geography)
						? geography
						: [geography];

					if (geographyArray.length === 0) return null;

					return (
						<span className="quick-filter">
							Countries:{" "}
							{geographyArray.length > 3
								? `${geographyArray.slice(0, 3).join(", ")} +${geographyArray.length - 3} more`
								: geographyArray.join(", ")}
						</span>
					);
				})()}
				{/* Data Period */}
				{filters?.period && (
					<span className="quick-filter">Period: {filters.period} days</span>
				)}

				{/* Platform */}
				{filters?.platform?.length > 0 && (
					<span className="quick-filter">
						Platform: {filters.platform.join(", ")}
					</span>
				)}

				{/* 2. APP CATEGORY */}
				{/* App Categories */}
				{filters?.categories?.length > 0 && (
					<span className="quick-filter">
						Categories:{" "}
						{filters.categories.length > 2
							? `${filters.categories.slice(0, 2).join(", ")} +${filters.categories.length - 2} more`
							: filters.categories.join(", ")}
					</span>
				)}

				{/* 3. APP METADATA */}
				{/* Developers */}
				{filters?.developer?.length > 0 && (
					<span className="quick-filter">
						Developers:{" "}
						{filters.developer.length > 2
							? `${filters.developer.slice(0, 2).join(", ")} +${filters.developer.length - 2} more`
							: filters.developer.join(", ")}
					</span>
				)}

				{/* Languages */}
				{filters?.languages?.length > 0 && (
					<span className="quick-filter">
						Languages:{" "}
						{filters.languages.length > 2
							? `${filters.languages.slice(0, 2).join(", ")} +${filters.languages.length - 2} more`
							: filters.languages.join(", ")}
					</span>
				)}

				{/* App Status */}
				{filters?.appStatus?.length > 0 && (
					<span className="quick-filter">
						Status: {filters.appStatus.join(", ")}
					</span>
				)}

				{/* App Type (Free/Paid) */}
				{filters?.isPaid !== null && filters?.isPaid !== undefined && (
					<span className="quick-filter">
						App Type:{" "}
						{filters.isPaid === true
							? "Paid"
							: filters.isPaid === false
								? "Free"
								: "Both"}
					</span>
				)}

				{/* App Features */}
				{filters?.features?.length > 0 && (
					<span className="quick-filter">
						Features:{" "}
						{filters.features.length > 2
							? `${filters.features.slice(0, 2).join(", ")} +${filters.features.length - 2} more`
							: filters.features.join(", ")}
					</span>
				)}

				{/* 4. CONTENT & SAFETY */}
				{/* Content Rating */}
				{filters?.contentRating?.length > 0 && (
					<span className="quick-filter">
						Content Rating: {filters.contentRating.join(", ")}
					</span>
				)}

				{/* Safety Tier */}
				{filters?.safetyTier?.length > 0 && (
					<span className="quick-filter">
						Safety: {filters.safetyTier.join(", ")}
					</span>
				)}

				{/* COPPA Compliance */}
				{filters?.coppa !== null && filters?.coppa !== undefined && (
					<span className="quick-filter">
						COPPA:{" "}
						{filters.coppa === true
							? "Compliant"
							: filters.coppa === false
								? "Non-Compliant"
								: "Both"}
					</span>
				)}

				{/* 5. APP POPULARITY */}
				{/* App Rating */}
				{filters?.Rating && filters.Rating > 1 && (
					<span className="quick-filter">
						Min. Rating: {filters.Rating.toFixed(1)}⭐
					</span>
				)}

				{/* Rating Count */}
				{filters?.RatingCount && filters.RatingCount > 0 && (
					<span className="quick-filter">
						Min. Reviews: {filters.RatingCount.toLocaleString()}
					</span>
				)}

				{/* Download Ranges */}
				{filters?.downloadRanges?.length > 0 && (
					<span className="quick-filter">
						Downloads: {filters.downloadRanges.join(", ")}
					</span>
				)}

				{/* App Age */}
				{filters?.appAge?.length > 0 && (
					<span className="quick-filter">Age: {filters.appAge.join(", ")}</span>
				)}

				{/* Last Updated */}
				{filters?.lastUpdated?.length > 0 && (
					<span className="quick-filter">
						Updated: {filters.lastUpdated.join(", ")}
					</span>
				)}

				{/* 6. PERFORMANCE METRICS */}
				{/* Performance Metrics */}
				{filters.minViewability !== 0 && filters.minViewability !== 80 && (
					<span className="quick-filter">
						Min. Viewability: {filters.minViewability}%
					</span>
				)}

				{/* Custom Performance Filters - Show column and threshold from actual structure */}
				{(() => {
					console.log(
						"🔍 Debug - filters.customFilters:",
						filters.customFilters,
					);

					// Check if customFilters exists and has items
					if (!filters.customFilters || filters.customFilters.length === 0) {
						console.log("⚠️ No custom filters found in props");
						return null;
					}

					return filters.customFilters.map((customFilter, index) => {
						console.log("🔍 Debug - Processing custom filter:", customFilter);

						// Simple mapping for common columns (you can expand this as needed)
						const getColumnLabel = (column) => {
							const columnMap = {
								cprmarketrequests: "Market Requests",
								impressions: "Total Impressions",
								viewables: "Viewables",
								viewabilitymeasures: "Viewability Measures",
								completefirstoccurrence: "Video Completions",
								startfirstoccurrence: "Video Starts",
								cprclicks: "CPR Clicks",
								fills: "Fills",
								nonzerobids: "Non Zero Bids",
								dealvolumedealinfo: "Deal Volume Deal Info",
								nonzerobidsvalueinusd: "Non Zero Bids Value (USD)",
								bidspendinusd: "Bid Spend (USD)",
								ctr: "CTR",
								vtr: "VTR",
								revenueperimpression: "Revenue Per Impression",
								costperimpression: "Cost Per Impression",
								bid_guidance: "Bid Guidance",
								bidCpm: "CPM",
								impressionswithclickmacro: "Impressions with Click Macro",
								cprmarketdemandpartnerspendinusd:
									"CPR Market Demand Partner Spend (USD)",
								cprmarketpublisherrevenueinusd:
									"CPR Market Publisher Revenue (USD)",
								cprallrequests: "CPR All Requests",
								cprprivatemarketimpressions: "CPR Private Market Impressions",
							};
							return columnMap[column] || column;
						};

						const columnsToMultiply = [
							"nonzerobidsvalueinusd",
							"bidspendinusd",
							"revenueperimpression",
							"costperimpression",
							"bid_guidance",
							"bidCpm",
						];

						const label = getColumnLabel(customFilter.column);
						const value = columnsToMultiply.includes(customFilter.column)
							? (customFilter.threshold || 0) / 10 ** 6
							: customFilter.threshold || 0;

						return (
							<span
								key={customFilter.id || `custom-${index}`}
								className="quick-filter custom-filter"
							>
								{label} &gt; {value}
							</span>
						);
					});
				})()}

				{/* Uploaded Bundle Lists */}
				{filters?.uploadedBundles?.includeList?.length > 0 && (
					<span className="quick-filter">
						Allow List: {filters.uploadedBundles.includeList.length} bundle IDs
					</span>
				)}

				{filters?.uploadedBundles?.excludeList?.length > 0 && (
					<span className="quick-filter">
						Block List: {filters.uploadedBundles.excludeList.length} bundle IDs
					</span>
				)}

				{/* Market Source (if still exists) */}
				{filters?.marketSource?.length > 0 && (
					<span className="quick-filter">
						Market Source: {filters.marketSource.join(", ")}
					</span>
				)}

				{/* MOVED TO END: Sort and Distribution Channel */}
				<span className="quick-filter">
					Sort:{" "}
					{sortOptions.find((opt) => opt.value === sortBy)?.label ||
						"Impressions"}{" "}
					({sortOrder === "desc" ? "Descending" : "Ascending"})
				</span>

				{/* Distribution Channel (locked for apps) */}
				<span className="quick-filter filter-locked">
					Distribution channel: App Store
				</span>

				{/* Show "No additional filters applied" if no optional filters are active */}
				{(() => {
					const hasAnyOptionalFilters = [
						filters?.mediaSubtype?.length > 0,
						filters?.deviceType?.length > 0,
						filters?.videoPlacement?.length > 0,
						filters?.geography?.length > 0,
						filters?.platform?.length > 0,
						filters?.appStatus?.length > 0,
						filters?.isPaid !== null && filters?.isPaid !== undefined,
						filters?.coppa !== null && filters?.coppa !== undefined,
						filters?.developers?.length > 0,
						filters?.contentRating?.length > 0,
						filters?.categories?.length > 0,
						filters?.languages?.length > 0,
						filters?.features?.length > 0,
						filters?.safetyTier?.length > 0,
						filters?.minRating && filters.minRating > 1,
						filters?.minRatingCount && filters.minRatingCount > 0,
						filters?.downloadRanges?.length > 0,
						filters?.appAge?.length > 0,
						filters?.lastUpdated?.length > 0,
						filters?.minViewability && filters.minViewability !== 80,
						filters?.minCTR && filters.minCTR !== 1.0,
						filters?.customFilters?.some((cf) => cf.threshold > 0),
						filters?.marketSource?.length > 0,
						filters?.uploadedBundles?.includeList?.length > 0,
						filters?.uploadedBundles?.excludeList?.length > 0,
						filters?.limit && filters.limit !== 5000,
						filters?.period && filters.period !== 30,
					].some(Boolean);

					return !hasAnyOptionalFilters ? (
						<span className="quick-filter no-filters">
							No additional filters applied
						</span>
					) : null;
				})()}
			</div>

			{/* Search Stage Indicator */}
			{refinedQuery && (
				<div className="search-stage-indicator">
					<span className="tag tag-orange">Refined Search</span>
					<span className="search-stage-text">
						App search refinement active
					</span>
					{onClearRefinement && (
						<button
							onClick={onClearRefinement}
							className="clear-refinement-btn"
						>
							<XIcon />
						</button>
					)}
				</div>
			)}

			<style jsx>{`
        .app-search-interface-container {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 100%;
          box-sizing: border-box;
        }

        .search-terms-display {
          margin-bottom: 12px;
          padding: 8px 12px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          min-height: 36px;
          max-height: 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          overflow: hidden;
          gap: 12px;
        }

        .search-terms-content {
          flex: 1;
          overflow: hidden;
        }

        .current-search-term {
          font-size: 13px;
          color: #374151;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .search-term {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          margin: 2px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
        }

        .term-include {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .term-exclude {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .term-remove-btn {
          margin-left: 4px;
          background: none;
          border: none;
          color: inherit;
          cursor: pointer;
          font-weight: bold;
          padding: 0 2px;
          border-radius: 2px;
          transition: background-color 0.2s;
        }

        .term-remove-btn:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .search-input-row {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .search-input-wrapper {
          position: relative;
          flex: 1;
          min-width: 200px;
          display: flex;
          align-items: center;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          padding: 0 8px 0 8px;
        }

        .search-input-wrapper :global(svg) {
          position: absolute;
          padding-left: 12px;
          width: 16px;
          height: 16px;
          color: #9ca3af;
          z-index: 1;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          padding: 12px 16px 12px 4px;
          font-size: 14px;
          background: transparent;
        }

        .search-input:focus {
          outline: none;
        }

        .search-input-wrapper:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .term-type-buttons {
          display: flex;
          gap: 4px;
          margin-right: 8px;
        }

        .term-type-btn {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          color: #6b7280;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .include-btn {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }

        .include-btn:hover:not(:disabled) {
          background: #059669;
          border-color: #059669;
        }

        .exclude-btn {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }

        .exclude-btn:hover:not(:disabled) {
          background: #dc2626;
          border-color: #dc2626;
        }

        .term-type-btn:disabled {
          background: #f3f4f6;
          color: #9ca3af;
          border-color: #d1d5db;
          cursor: not-allowed;
        }

        .tier-input-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 140px;
          max-width: 200px;
          position: relative;
          flex-shrink: 0;
        }

        .search-button-container {
          display: flex;
          align-items: flex-end;
          flex-shrink: 0;
        }

        .search-button {
          padding: 12px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          min-width: 100px;
        }

        .search-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .search-button:disabled {
          background: #d1d5db;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .loading-spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .btn-clear-all-inline {
          padding: 4px 12px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .btn-clear-all-inline:hover {
          background: #dc2626;
        }

        .performance-filter-section {
          margin-bottom: 20px;
        }

        .performance-filter-row {
          display: flex;
          gap: 16px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .performance-filter-container,
        .target-container,
        .sort-container {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 120px;
        }

        .performance-filter-container {
          flex: 1;
        }

        .target-container {
          flex: 1.2;
        }

        .sort-container {
          flex: 1;
        }

        .performance-label-container {
          display: flex;
          align-items: center;
          height: 20px;
        }

        .performance-filter-label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin: 0;
          white-space: nowrap;
        }

        .performance-filter-select,
        .sort-select {
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          background: white;
          color: #374151;
          outline: none;
          cursor: pointer;
        }

        .performance-filter-select:focus,
        .sort-select:focus {
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .target-input-group {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: nowrap;
        }

        .target-value-input {
          width: 100%;
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          background: white;
          color: #374151;
          outline: none;
          text-align: center;
        }

        .target-value-input:focus {
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .sort-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .sort-select {
          flex: 1;
          min-width: 100px;
          border: 1px solid #d1d5db;
        }

        .sort-order-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
          transition: all 0.2s ease;
          user-select: none;
          flex-shrink: 0;
        }

        .sort-order-arrow:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
          color: #111827;
        }

        .sort-order-arrow:active {
          background: #d1d5db;
          transform: scale(0.95);
        }

        .search-stage-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding: 8px 12px;
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 6px;
        }

        .tag {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .tag-orange {
          background: #fed7aa;
          color: #9a3412;
        }

        .search-stage-text {
          flex: 1;
          font-size: 14px;
          color: #92400e;
        }

        .clear-refinement-btn {
          background: none;
          border: none;
          color: #92400e;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .clear-refinement-btn:hover {
          background: rgba(146, 64, 14, 0.1);
        }

        .quick-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          align-items: center;
        }

        .quick-filter {
          padding: 4px 8px;
          background: #f3f4f6;
          color: #6b7280;
          border-radius: 4px;
          font-size: 11px;
          border: 1px solid #e5e7eb;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .filter-locked {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
          font-weight: 500;
          position: relative;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .filter-locked::after {
          content: "📱";
          margin-left: 4px;
        }

        .no-filters {
          background: #e5e7eb;
          color: #6b7280;
          border: 1px solid #d1d5db;
          font-style: italic;
        }

        .app-search-interface-container * {
          box-sizing: border-box;
        }

        @media (max-width: 1024px) {
          .search-input-row {
            gap: 8px;
          }
          
          .tier-input-container {
            min-width: 120px;
          }
        }

        @media (max-width: 768px) {
          .app-search-interface-container {
            padding: 16px;
          }

          .search-input-row {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .search-input-wrapper {
            width: 100%;
            min-width: auto;
          }

          .tier-input-container,
          .search-button-container {
            width: 100%;
            align-items: stretch;
            min-width: auto;
            max-width: none;
          }

          .search-button {
            width: 100%;
            justify-content: center;
          }

          .performance-filter-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          
          .performance-filter-container,
          .target-container,
          .sort-container {
            width: 100%;
            align-items: flex-start;
            flex: none;
            min-width: auto;
          }

          .performance-filter-select,
          .sort-select {
            width: 100%;
          }

          .target-input-group,
          .sort-controls {
            width: 100%;
          }

          .target-input-group {
            flex-direction: row;
            align-items: stretch;
            gap: 8px;
          }
          
          .target-value-input {
            width: 100%;
            flex: 1;
          }

          .term-type-buttons {
            margin-right: 4px;
          }

          .term-type-btn {
            padding: 4px 8px;
            font-size: 11px;
          }

          .search-terms-display {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            min-height: auto;
            max-height: none;
          }

          .btn-clear-all-inline {
            align-self: flex-end;
            padding: 6px 12px;
          }

          .quick-filters {
            gap: 4px;
          }

          .quick-filter {
            font-size: 10px;
            padding: 3px 6px;
          }
        }

        @media (max-width: 480px) {
          .app-search-interface-container {
            padding: 12px;
          }
          
          .search-input-row {
            gap: 8px;
          }

          .term-type-buttons {
            flex-wrap: wrap;
            gap: 2px;
          }

          .term-type-btn {
            padding: 4px 8px;
            font-size: 11px;
            flex: 1;
            min-width: 60px;
          }

          .target-input-group {
            flex-direction: column;
            gap: 6px;
          }
          
          .target-value-input {
            width: 100%;
          }
        }
      `}</style>
		</div>
	);
};
